import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Wallet, History, CheckCircle2, ExternalLink, Trash2, BookOpen } from 'lucide-react';
import { api } from '../lib/api';
import Modal from '../components/ui/Modal';

// Partner Investments: what each partner has put into the business and whether the
// Company has paid it back (Operating Agreement 5.2 / 6.2). Server enforces who can
// edit what; the UI only hides buttons that would be refused.

const KIND_LABEL = {
  reimbursable_expense: 'Reimbursable expense',
  capital_contribution: 'Capital contribution',
  loan_to_company: 'Loan to company',
};
const STATUS_LABEL = {
  unreimbursed: 'Owed',
  partially_reimbursed: 'Partly repaid',
  reimbursed: 'Reimbursed',
  disputed: 'Needs confirmation',
  not_reimbursable: 'Not reimbursable',
};
const STATUS_CLASS = {
  unreimbursed: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  partially_reimbursed: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  reimbursed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  disputed: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  not_reimbursable: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-200',
};
// PENDING KEVIN (LP-350 Q6): the accountant's per-row answer for a row before the cutover (blank = not answered).
const DISPOSITION_LABEL = { covered: 'Covered by an opening balance', settled_outside: 'Settled outside the books' };
const FIELD_LABEL = { books_entry_id: 'Books entry', covered_by_opening: 'covered by the opening balance', pre_cutover_disposition: 'answer before the cutover' };
const ACTION_LABEL = { create: 'Added', import: 'Imported', update: 'Edited', status: 'Status changed', reimburse: 'Marked reimbursed', delete: 'Deleted', restore: 'Restored' };

export const usd = (c) => `$${((c || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// "1,123.20" → 112320; null when not a valid dollar amount. String math, no float drift.
export function toCents(v) {
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(String(v ?? '').replace(/[$,\s]/g, ''));
  return m ? Number(m[1]) * 100 + Number((m[2] || '0').padEnd(2, '0')) : null;
}
const toDollars = (c) => (c == null ? '' : (c / 100).toFixed(2));
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const safeHref = (u) => (/^https?:\/\//i.test(u || '') ? u : null);

function canEdit(me, e) {
  if (me.can_approve) return true;
  return !!me.member && e.member === me.member && !(e.reimbursed_cents > 0) && !['reimbursed', 'partially_reimbursed'].includes(e.status);
}

// LP-309 slice B: where the row stands in Books. Covered = inside the 1/1/2026 opening payback
// balance (its creation never posts); "In Books" = its entry was posted by the database.
function BooksChip({ e }) {
  if (e.pre_cutover_disposition === 'settled_outside') return <span className="inline-block whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 dark:bg-slate-700 dark:text-slate-200" title="Settled outside the books before the cutover: not owed, never posted">Settled outside</span>;
  if (e.covered_by_opening) return <span className="inline-block whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 dark:bg-slate-700 dark:text-slate-200" title="Inside the 1/1/2026 opening payback balance: never posted again">Opening balance</span>;
  if (e.books_entry_id) return <Link to="/books?tab=paybacks" className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-sky-50 px-2 py-0.5 text-xs text-sky-800 hover:underline dark:bg-sky-900/40 dark:text-sky-200"><BookOpen size={11} /> In Books</Link>;
  return null;
}

function StatusChip({ status, kind }) {
  if (kind === 'capital_contribution' && status === 'not_reimbursable') {
    return <span className="inline-block whitespace-nowrap rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200">Capital</span>;
  }
  return <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[status] || ''}`}>{STATUS_LABEL[status] || status}</span>;
}

export default function InvestmentsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [member, setMember] = useState('');
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // {} = new, entry = edit
  const [paying, setPaying] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);

  const load = useCallback(async () => {
    try { setData(await api.get('/investments')); setError(''); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.entries.filter((e) => (!member || e.member === member) && (!status || e.status === status) && (!kind || e.kind === kind)
      && (!needle || [e.vendor, e.description, e.category, e.note, e.on_behalf_of].some((x) => String(x || '').toLowerCase().includes(needle))));
  }, [data, member, status, kind, q]);
  const shownTotal = rows.reduce((n, e) => n + e.amount_cents, 0);

  if (error && !data) return <div className="p-4 text-rose-600">{error}</div>;
  if (!data) return <div className="p-4 text-[var(--text-secondary)]">Loading investments…</div>;
  const { me, summary } = data;

  const select = 'rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm text-[var(--text-primary)] dark:bg-slate-800';

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--text-primary)]"><Wallet size={20} /> Partner Investments</h1>
          <p className="text-sm text-[var(--text-secondary)]">Money each partner has put into CCS Surfaces, and what the Company has paid back.</p>
        </div>
        <button onClick={() => setEditing({})} className="flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white">
          <Plus size={15} /> Add entry
        </button>
      </div>
      {error && <p className="mb-3 text-sm text-rose-600">{error}</p>}

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {summary.map((s) => (
          <button key={s.member} onClick={() => setMember(member === s.member ? '' : s.member)}
            className={`rounded-xl border bg-white p-4 text-left dark:bg-slate-900 ${member === s.member ? 'border-primary ring-1 ring-primary' : 'border-[var(--border)]'}`}>
            <div className="mb-2 flex items-baseline justify-between">
              <span className="font-semibold text-[var(--text-primary)]">{s.member}</span>
              <span className="text-xs text-[var(--text-secondary)]">{s.count} {s.count === 1 ? 'entry' : 'entries'}</span>
            </div>
            <dl className="grid grid-cols-3 gap-2 text-xs">
              <div><dt className="text-[var(--text-secondary)]">Put in</dt><dd className="text-sm font-semibold tabular-nums text-[var(--text-primary)]">{usd(s.put_in_cents)}</dd></div>
              <div><dt className="text-[var(--text-secondary)]">Reimbursed</dt><dd className="text-sm font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">{usd(s.reimbursed_cents)}</dd></div>
              <div><dt className="text-[var(--text-secondary)]">Still owed</dt><dd className="text-sm font-semibold tabular-nums text-amber-700 dark:text-amber-400">{usd(s.owed_cents)}</dd></div>
            </dl>
            {(s.capital_cents > 0 || s.needs_confirmation_count > 0 || s.settled_outside_count > 0) && (
              <p className="mt-2 text-xs text-[var(--text-secondary)]">
                {s.capital_cents > 0 && <>Capital: {usd(s.capital_cents)}. </>}
                {s.needs_confirmation_count > 0 && <span className="text-rose-700 dark:text-rose-300">{s.needs_confirmation_count} needing confirmation ({usd(s.needs_confirmation_cents)}), not counted above. </span>}
                {s.settled_outside_count > 0 && <>Settled outside the books: {usd(s.settled_outside_cents)}, not owed.</>}
              </p>
            )}
          </button>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className={select} value={member} onChange={(e) => setMember(e.target.value)} aria-label="Member">
          <option value="">All partners</option>
          {summary.map((s) => <option key={s.member} value={s.member}>{s.member}</option>)}
        </select>
        <select className={select} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">Any status</option>
          {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className={select} value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind">
          <option value="">Any kind</option>
          {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input className={`${select} min-w-0 flex-1 sm:max-w-xs`} placeholder="Search vendor, description, note" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="ml-auto text-xs text-[var(--text-secondary)]">{rows.length} shown · {usd(shownTotal)}</span>
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900 md:block">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-[var(--text-secondary)] dark:bg-slate-800">
            <tr>
              <th className="px-3 py-2">Date</th><th className="px-3 py-2">Partner</th><th className="px-3 py-2">Vendor / description</th>
              <th className="px-3 py-2">Category</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2">Status</th><th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id} className="border-t border-[var(--border)] align-top">
                <td className="whitespace-nowrap px-3 py-2 text-[var(--text-secondary)]">{fmtDate(e.entry_date)}</td>
                <td className="px-3 py-2">{e.member}{e.on_behalf_of && <div className="text-xs text-[var(--text-secondary)]">via {e.on_behalf_of}</div>}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-[var(--text-primary)]">{e.vendor || e.description}</div>
                  {e.vendor && <div className="text-xs text-[var(--text-secondary)]">{e.description}</div>}
                  {e.note && <div className="mt-0.5 text-xs italic text-[var(--text-secondary)]">{e.note}</div>}
                </td>
                <td className="px-3 py-2 text-xs text-[var(--text-secondary)]">{e.category}<div>{KIND_LABEL[e.kind]}</div></td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{usd(e.amount_cents)}
                  {e.reimbursed_cents > 0 && e.reimbursed_cents < e.amount_cents && <div className="text-xs text-emerald-700">repaid {usd(e.reimbursed_cents)}</div>}
                </td>
                <td className="px-3 py-2"><StatusChip status={e.status} kind={e.kind} />
                  {e.reimbursed_date && <div className="mt-0.5 text-xs text-[var(--text-secondary)]">{fmtDate(e.reimbursed_date)}{e.reimbursement_ref ? ` · ${e.reimbursement_ref}` : ''}</div>}
                  <div className="mt-1"><BooksChip e={e} /></div>
                </td>
                <td className="px-3 py-2"><RowActions e={e} me={me} onEdit={setEditing} onPay={setPaying} onHistory={setHistoryOf} /></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-[var(--text-secondary)]">No entries match.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Phone cards */}
      <div className="space-y-2 md:hidden">
        {rows.map((e) => (
          <div key={e.id} className="rounded-xl border border-[var(--border)] bg-white p-3 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-[var(--text-primary)]">{e.vendor || e.description}</div>
                <div className="text-xs text-[var(--text-secondary)]">{e.member} · {fmtDate(e.entry_date)}{e.category ? ` · ${e.category}` : ''}</div>
              </div>
              <div className="shrink-0 text-right"><div className="font-semibold tabular-nums">{usd(e.amount_cents)}</div><StatusChip status={e.status} kind={e.kind} /></div>
            </div>
            {e.vendor && <div className="mt-1 text-xs text-[var(--text-secondary)]">{e.description}</div>}
            {e.note && <div className="mt-1 text-xs italic text-[var(--text-secondary)]">{e.note}</div>}
            {(e.covered_by_opening || e.books_entry_id || e.pre_cutover_disposition === 'settled_outside') && <div className="mt-1"><BooksChip e={e} /></div>}
            <div className="mt-2"><RowActions e={e} me={me} onEdit={setEditing} onPay={setPaying} onHistory={setHistoryOf} /></div>
          </div>
        ))}
        {!rows.length && <p className="py-6 text-center text-sm text-[var(--text-secondary)]">No entries match.</p>}
      </div>

      {editing && <EntryForm entry={editing} me={me} books={data.books || {}} members={summary.map((s) => s.member)} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {paying && <ReimburseForm entry={paying} onClose={() => setPaying(null)} onSaved={() => { setPaying(null); load(); }} />}
      {historyOf && <HistoryModal entry={historyOf} onClose={() => setHistoryOf(null)} />}
    </div>
  );
}

function RowActions({ e, me, onEdit, onPay, onHistory }) {
  const btn = 'inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs';
  const href = safeHref(e.evidence_url);
  return (
    <div className="flex flex-wrap items-center gap-1">
      {canEdit(me, e) && <button className={btn} onClick={() => onEdit(e)}>Edit</button>}
      {me.can_approve && ['reimbursable_expense', 'loan_to_company'].includes(e.kind) && ['unreimbursed', 'partially_reimbursed'].includes(e.status) && e.pre_cutover_disposition !== 'settled_outside' && (
        <button className={`${btn} text-emerald-700 dark:text-emerald-400`} onClick={() => onPay(e)}><CheckCircle2 size={12} /> Mark reimbursed</button>
      )}
      <button className={btn} onClick={() => onHistory(e)} aria-label="History"><History size={12} /></button>
      {href && <a className={btn} href={href} target="_blank" rel="noopener noreferrer" aria-label="Evidence"><ExternalLink size={12} /></a>}
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <label className="block text-xs font-medium text-[var(--text-secondary)]">{label}
      <input {...props} className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-2 text-sm text-[var(--text-primary)] dark:bg-slate-800" />
    </label>
  );
}
function Select({ label, options, ...props }) {
  return (
    <label className="block text-xs font-medium text-[var(--text-secondary)]">{label}
      <select {...props} className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-2 text-sm text-[var(--text-primary)] dark:bg-slate-800">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function EntryForm({ entry, me, books, members, onClose, onSaved }) {
  const isNew = !entry.id;
  const [f, setF] = useState({
    member: entry.member || me.member || members[0],
    on_behalf_of: entry.on_behalf_of || '',
    entry_date: entry.entry_date || new Date().toISOString().slice(0, 10),
    kind: entry.kind || 'reimbursable_expense',
    category: entry.category || '',
    vendor: entry.vendor || '',
    description: entry.description || '',
    amount: toDollars(entry.amount_cents),
    status: entry.status || 'unreimbursed',
    evidence_url: entry.evidence_url || '',
    note: entry.note || '',
    covered_by_opening: !!entry.covered_by_opening,
    pre_cutover_disposition: entry.pre_cutover_disposition || '',
  });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (ev) => setF({ ...f, [k]: ev.target.value });

  async function save(ev) {
    ev.preventDefault();
    const cents = toCents(f.amount);
    if (!cents) { setErr('Enter the amount in dollars, e.g. 123.45'); return; }
    const body = {
      on_behalf_of: f.on_behalf_of, entry_date: f.entry_date, kind: f.kind, category: f.category,
      vendor: f.vendor, description: f.description, amount_cents: cents, evidence_url: f.evidence_url, note: f.note,
    };
    if (me.can_approve) { body.member = f.member; body.status = f.status; }
    if (!isNew && me.can_approve && f.status === entry.status) delete body.status;
    if (!isNew && body.member === entry.member) delete body.member;
    if (me.can_approve && f.covered_by_opening !== !!entry.covered_by_opening) body.covered_by_opening = f.covered_by_opening;
    if (books.can_edit && f.pre_cutover_disposition !== (entry.pre_cutover_disposition || '')) body.pre_cutover_disposition = f.pre_cutover_disposition || null;
    setBusy(true);
    try {
      if (isNew) await api.post('/investments', body);
      else await api.patch(`/investments/${entry.id}`, { ...body, revision: entry.updated_at });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  async function del() {
    if (!window.confirm('Delete this entry? It stays in the history and Chad can restore it.')) return;
    setBusy(true);
    try { await api.delete(`/investments/${entry.id}`); onSaved(); } catch (e) { setErr(e.message); setBusy(false); }
  }

  // PENDING KEVIN (LP-350 Q2): rows can be covered only through books_covered_through() (the
  // cutover until the accountant answers); the database refuses later ones.
  const through = books.covered_through || null;
  const coverClosed = !!through && f.entry_date > through && !entry.covered_by_opening;
  // PENDING KEVIN (LP-350 Q6): the per-row answer, for a row dated on or before the cutover.
  const askAnswer = books.can_edit && ((!!books.cutover_date && f.entry_date <= books.cutover_date) || !!entry.pre_cutover_disposition);
  const setAnswer = (v) => setF({ ...f, pre_cutover_disposition: v, covered_by_opening: v === 'covered' ? true : v === 'settled_outside' ? false : f.covered_by_opening });
  // "Settled outside the books" only for Steve's and Chase's costs and loans still owed (the
  // service and the database refuse it on any other row); a row that has it can still clear it.
  const settledFits = ['Steve', 'Chase'].includes(f.member) && ['reimbursable_expense', 'loan_to_company'].includes(f.kind) && ['unreimbursed', 'partially_reimbursed'].includes(f.status);
  const answerOptions = Object.entries(DISPOSITION_LABEL).filter(([k]) => k !== 'settled_outside' || settledFits || f.pre_cutover_disposition === 'settled_outside');

  // Statuses that carry a repayment are set through "Mark reimbursed", not here.
  const statusOptions = ['unreimbursed', 'disputed', 'not_reimbursable']
    .concat(['reimbursed', 'partially_reimbursed'].includes(entry.status) ? [entry.status] : [])
    .map((k) => [k, STATUS_LABEL[k]]);

  return (
    <Modal title={isNew ? 'Add entry' : 'Edit entry'} onClose={onClose}>
      <form onSubmit={save} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {me.can_approve
          ? <Select label="Partner" value={f.member} onChange={set('member')} options={members.map((m) => [m, m])} />
          : <Input label="Partner" value={f.member} disabled />}
        <Input label="On behalf of (company, optional)" value={f.on_behalf_of} onChange={set('on_behalf_of')} placeholder="e.g. Concreate Inc" />
        <Input label="Date" type="date" required value={f.entry_date} onChange={set('entry_date')} />
        <Input label="Amount ($)" inputMode="decimal" required value={f.amount} onChange={set('amount')} placeholder="0.00" />
        <Select label="Kind" value={f.kind} onChange={(ev) => {
          const k = ev.target.value;
          let st = f.status;
          if (k === 'capital_contribution' && st === 'unreimbursed') st = 'not_reimbursable';
          if (k !== 'capital_contribution' && f.kind === 'capital_contribution' && st === 'not_reimbursable') st = 'unreimbursed';
          setF({ ...f, kind: k, status: st });
        }} options={Object.entries(KIND_LABEL)} />
        {me.can_approve
          ? <Select label="Status" value={f.status} onChange={set('status')} options={statusOptions} />
          : <Input label="Status" value={STATUS_LABEL[f.status]} disabled />}
        <Input label="Vendor" value={f.vendor} onChange={set('vendor')} />
        <Input label="Category" value={f.category} onChange={set('category')} placeholder="e.g. Shipping & Delivery" />
        <div className="sm:col-span-2"><Input label="Description" required value={f.description} onChange={set('description')} /></div>
        <div className="sm:col-span-2"><Input label="Evidence link (receipt, invoice, statement)" type="url" value={f.evidence_url} onChange={set('evidence_url')} placeholder="https://" /></div>
        {me.can_approve && (
          <label className="flex items-start gap-2 text-sm text-[var(--text-primary)] sm:col-span-2">
            <input type="checkbox" className="mt-0.5" checked={f.covered_by_opening} disabled={coverClosed} onChange={(ev) => setF({ ...f, covered_by_opening: ev.target.checked })} />
            <span>Covered by the opening balance<span className="block text-xs text-[var(--text-secondary)]">Inside the 1/1/2026 payback owed to the partner, so Books never posts it again. Its repayment still posts.{through && ` Only for rows dated on or before ${fmtDate(through)}; later rows post from Partner Investments.`}</span></span>
          </label>
        )}
        {askAnswer && (
          <div className="sm:col-span-2">
            <Select label="Before the cutover: the accountant's answer for this row" value={f.pre_cutover_disposition} onChange={(ev) => setAnswer(ev.target.value)}
              options={[['', 'Not answered yet'], ...answerOptions]} />
            <p className="mt-1 text-xs text-[var(--text-secondary)]">Covered: inside the partner's opening balance (ticks the box above). Settled outside the books (Steve's and Chase's costs and loans still owed): not owed, and no repayment is recorded.</p>
          </div>
        )}
        <label className="block text-xs font-medium text-[var(--text-secondary)] sm:col-span-2">Note
          <textarea value={f.note} onChange={set('note')} rows={2} className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-2 text-sm text-[var(--text-primary)] dark:bg-slate-800" />
        </label>
        {err && <p className="text-sm text-rose-600 sm:col-span-2">{err}</p>}
        <div className="flex items-center justify-between gap-2 sm:col-span-2">
          {!isNew ? <button type="button" disabled={busy} onClick={del} className="inline-flex items-center gap-1 text-sm text-rose-600"><Trash2 size={14} /> Delete</button> : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm">Cancel</button>
            <button disabled={busy} className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function ReimburseForm({ entry, onClose, onSaved }) {
  const [amount, setAmount] = useState(toDollars(entry.amount_cents));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dateTouched, setDateTouched] = useState(false);
  const [ref, setRef] = useState(entry.reimbursement_ref || '');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  async function save(ev) {
    ev.preventDefault();
    const cents = toCents(amount);
    if (!cents) { setErr('Enter the amount repaid so far, e.g. 123.45'); return; }
    setBusy(true);
    try {
      // The date goes with a new repayment or when changed here; a lower total keeps the recorded date.
      const body = { amount_cents: cents, reimbursement_ref: ref, revision: entry.updated_at };
      if (dateTouched || cents > (entry.reimbursed_cents || 0)) body.reimbursed_date = date;
      await api.post(`/investments/${entry.id}/reimburse`, body);
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  }
  return (
    <Modal title="Mark reimbursed" onClose={onClose} maxWidth="max-w-md">
      <p className="mb-3 text-sm text-[var(--text-secondary)]">{entry.member} · {entry.vendor || entry.description} · {usd(entry.amount_cents)}</p>
      <form onSubmit={save} className="space-y-3">
        <Input label="Total repaid so far ($)" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Input label="Date repaid" type="date" value={date} onChange={(e) => { setDate(e.target.value); setDateTouched(true); }} />
        <Input label="Reference (check no., transfer, memo)" value={ref} onChange={(e) => setRef(e.target.value)} />
        {err && <p className="text-sm text-rose-600">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm">Cancel</button>
          <button disabled={busy} className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Save</button>
        </div>
      </form>
    </Modal>
  );
}

function fmtVal(k, v) {
  if (k === 'books_entry_id') return v ? 'posted' : 'not posted';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (k === 'pre_cutover_disposition') return DISPOSITION_LABEL[v] || 'not answered';
  if (v == null || v === '') return 'blank';
  if (k.endsWith('_cents')) return usd(v);
  if (k === 'status') return STATUS_LABEL[v] || v;
  if (k === 'kind') return KIND_LABEL[v] || v;
  return String(v);
}

function HistoryModal({ entry, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get(`/investments/${entry.id}/history`).then((r) => setRows(r.history || [])).catch((e) => setErr(e.message));
  }, [entry.id]);
  return (
    <Modal title="History" onClose={onClose} maxWidth="max-w-lg">
      <p className="mb-3 text-sm text-[var(--text-secondary)]">{entry.vendor || entry.description} · {usd(entry.amount_cents)}</p>
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!rows && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      <ol className="space-y-3">
        {(rows || []).map((h) => (
          <li key={h.id} className="rounded-lg border border-[var(--border)] p-2 text-xs">
            <div className="mb-1 font-medium text-[var(--text-primary)]">{ACTION_LABEL[h.action] || h.action} · {h.actor || 'system'} · {new Date(h.created_at).toLocaleString()}</div>
            {['update', 'status', 'reimburse'].includes(h.action) && h.after && Object.keys(h.after).map((k) => (
              <div key={k} className="text-[var(--text-secondary)]">{FIELD_LABEL[k] || k.replace(/_/g, ' ')}: {fmtVal(k, h.before?.[k])} → {fmtVal(k, h.after[k])}</div>
            ))}
          </li>
        ))}
      </ol>
    </Modal>
  );
}
